import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useGlobalContext } from './utils/GlobalContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Chaos.css';
import { startChaos, stopChaos } from './utils/chaosEngine';
import { startWarp, stopWarp } from './utils/pixelWarp';
import { isMelodyMuted, setMelodyMuted } from './utils/hoverMelody';


// The wordmark is the Letters project. Each character is a photographed
// letterform and each one flicks through its own set on an irregular clock,
// so no two letters change in step. window.LETTERS comes from
// public/letters/manifest.js.
function LettersLogo() {
  const sets = (typeof window !== 'undefined' && window.LETTERS) || {};
  const chars = 'LUKEABC'.split('');

  const pools = useMemo(() => chars.map((ch) => {
    const files = sets[ch] || [];
    return files.map((f) => `letters/${ch}/${f}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const pick = useCallback((pool) => pool.length
    ? pool[Math.floor(Math.random() * pool.length)]
    : null, []);

  const [srcs, setSrcs] = useState(() => pools.map(pick));

  useEffect(() => {
    // preload so a swap never flashes
    pools.forEach((pool) => pool.forEach((src) => { const i = new Image(); i.src = src; }));

    const timers = pools.map((pool, index) => {
      if (pool.length < 2) return null;
      let id;
      const tick = (delay) => {
        id = setTimeout(() => {
          setSrcs((prev) => {
            const next = prev.slice();
            let candidate = pick(pool);
            let guard = 0;
            while (candidate === prev[index] && guard++ < 8) candidate = pick(pool);
            next[index] = candidate;
            return next;
          });
          tick();
        }, delay !== undefined ? delay : 1400 + Math.random() * 2600);
      };
      // stagger the first change so the letters never move together
      tick(index * 320 + Math.random() * 1200);
      return () => clearTimeout(id);
    });

    return () => timers.forEach((stop) => stop && stop());
  }, [pools, pick]);

  return (
    <span className="title-text letters-logo" aria-label="Lukeabc">
      {chars.map((ch, index) => (
        <span key={index} className="title-letter" style={{ '--letter-index': index }}>
          {srcs[index]
            ? <img src={srcs[index]} alt="" className="letters-logo-img" draggable="false" />
            : ch}
        </span>
      ))}
    </span>
  );
}

export default function Header() {
  const { numbersActive, imagesActive, tableActive, handleNumberClick, handleImageClick, handleTableClick, darkMode, toggleDarkMode } = useGlobalContext();
  const location = useLocation();
  const navigate = useNavigate();

  const { thingsActive, infoActive, infoSubPage, isDetailPage } = useMemo(() => {
    const isInfoPage = location.pathname === '/info';
    const isInfoSubPage = ['/links', '/tos', '/versions', '/storage'].includes(location.pathname);
    return {
      thingsActive: location.pathname === '/',
      infoActive: isInfoPage || isInfoSubPage,
      infoSubPage: isInfoSubPage,
      isDetailPage: location.pathname.includes('/work/'),
    };
  }, [location.pathname]);


  // Chaos mode, ported from the v1 site. Remembered across pages like it was.
  const [chaos, setChaos] = React.useState(
    () => { try { return localStorage.getItem('mode-chaos') === '1'; } catch (e) { return false; } }
  );

  // the warping checkerboard is part of chaos now: off, the page sits on a
  // plain background; on, the whole thing goes to checks
  React.useEffect(() => {
    if (chaos) { startChaos(); startWarp(); } else { stopChaos(); stopWarp(); }
    try { localStorage.setItem('mode-chaos', chaos ? '1' : '0'); } catch (e) {}
  }, [chaos]);

  React.useEffect(() => () => { stopChaos(); stopWarp(); }, []);

  // the hover keyboard, on or off. Remembered like chaos mode is.
  const [sound, setSound] = React.useState(() => !isMelodyMuted());
  React.useEffect(() => { setMelodyMuted(!sound); }, [sound]);

  return (
    <header>
      <div className="header-container">
        <div className="title">
          <Link to={
            isDetailPage && location.state?.fromTrait
              ? `/?trait_type=${encodeURIComponent(location.state.fromTrait.traitType)}&trait_value=${encodeURIComponent(location.state.fromTrait.traitValue)}`
              : isDetailPage ? '/'
              : infoSubPage ? '/info'
              : infoActive ? '/'
              : location.search.includes('trait_type') ? '/'
              : '/info'
          } className="title-link">
            <LettersLogo />
          </Link>
        </div>
        <div className="header-controls">
          {thingsActive && <>
          <div className="collection-viewer">
            <button className={`number-view ${numbersActive ? 'active' : ''}`} onClick={handleNumberClick}>#</button>
          </div>
          <div className="collection-viewer">
            {/* The index. Rules and a divider, so it reads as a table rather
                than as another list. */}
            <button
              className={`table-view ${tableActive ? 'active' : ''}`}
              onClick={handleTableClick}
              title="Index"
              aria-label="Index"
            >
              <svg viewBox="0 0 22 18" className="icon icon-table" aria-hidden="true">
                <rect x="1" y="1" width="20" height="16" rx="0.5" />
                <path d="M1 6h20M1 11h20M9 1v16" />
              </svg>
            </button>
          </div>
          <div className="collection-viewer">
            <button className={`image-view ${imagesActive ? 'active' : ''}`} onClick={handleImageClick}>
              <svg xmlns="http://www.w3.org/2000/svg" width="122" height="146" viewBox="0 0 122 146" className="icon icon-image">
                <defs></defs>
                {/* the page behind the picture. Left unfilled so the
                    checkerboard shows through, like the other icons */}
                <path d="M6.71 139V7h62.52l45.48 41.52V139z" style={{ strokeWidth: 0, fill: 'none' }}></path>
                <path d="m66.52 14 41.19 37.61V132h-94V14zm5.42-14H-.29v146h122V45.43z" className="cls-1"></path>
                <path d="M65.71 0v52h56" className="cls-2"></path>
                <path d="M20.37 125h81.26v-23.02L77.8 78.15l-22.16 22.17-11.46-11.46-23.81 24.5z" className="cls-1"></path>
                <circle cx="32.49" cy="70.12" r="12.12" className="cls-1"></circle>
              </svg>
            </button>
          </div>
          </>}
        <button
          onClick={() => setSound((on) => !on)}
          className={`sound-toggle ${sound ? 'active' : ''}`}
          title={sound ? 'Turn the keyboard off' : 'Turn the keyboard on'}
          aria-pressed={sound}
        >
          <img src="music.png" alt="" className="sound-toggle-icon" draggable="false" />
        </button>
        <button
          onClick={() => setChaos((on) => !on)}
          className={`chaos-toggle ${chaos ? 'active' : ''}`}
          title={chaos ? 'Leave chaos mode' : 'Chaos mode'}
          aria-pressed={chaos}
        >
          <img src="chaos/skull.png" alt="" className="chaos-toggle-icon" draggable="false" />
        </button>
        <button onClick={toggleDarkMode} className={`dark-mode-toggle ${darkMode ? 'active' : ''}`} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
          {darkMode ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          )}
        </button>
        </div>
      </div>
    </header>
  );
};
